import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FiBell, 
  FiTrendingUp, 
  FiShield, 
  FiZap, 
  FiCheckCircle,
  FiArrowRight,
  FiMonitor,
  FiAlertTriangle,
  FiBarChart2,
  FiTarget
} from 'react-icons/fi';
import { trackPageView } from '../utils/analytics';
import './AIAlerting.css';

const AIAlerting = () => {
  useEffect(() => {
    // Track page visit with comprehensive analytics
    trackPageView('/ai-alerting', {
      page_title: 'LogCrest AI Alerting - Smart Alerting & Dashboard',
      page_category: 'ai-alerting'
    });
  }, []);

  const features = [
    {
      icon: <FiBell />,
      title: 'Smart Alerting',
      description: 'AI-powered alerting that reduces noise by 90% and only notifies you of critical issues.'
    },
    {
      icon: <FiTrendingUp />,
      title: 'Predictive Analytics',
      description: 'Predict potential issues before they impact your systems with machine learning insights.'
    },
    {
      icon: <FiShield />,
      title: 'Anomaly Detection',
      description: 'Automatically detect unusual patterns and behaviors across your entire infrastructure.'
    },
    {
      icon: <FiZap />,
      title: 'Real-time Dashboards',
      description: 'Customizable dashboards with real-time data visualization and interactive charts.'
    }
  ];

  const capabilities = [
    'Intelligent alert correlation and grouping',
    'Machine learning-based anomaly detection',
    'Predictive failure analysis',
    'Custom alert rules and thresholds',
    'Multi-channel notification delivery',
    'Alert fatigue reduction algorithms',
    'Historical trend analysis',
    'Automated incident response workflows'
  ];

  const benefits = [
    {
      metric: '90%',
      label: 'Reduction in Alert Noise',
      description: 'AI filters out false positives'
    },
    {
      metric: '75%',
      label: 'Faster Incident Response',
      description: 'Predictive alerts enable proactive action'
    },
    {
      metric: '60%',
      label: 'Cost Savings',
      description: 'Prevent outages before they happen'
    },
    {
      metric: '99.9%',
      label: 'System Reliability',
      description: 'Proactive monitoring ensures uptime'
    }
  ];

  return (
    <div className="ai-alerting">
      {/* Hero Section */}
      <section className="alerting-hero">
        <div className="container">
          <div className="alerting-hero-content">
            <motion.div
              className="hero-text"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>AI Alerting & Dashboard</h1>
              <p>
                Transform your monitoring with intelligent alerting and powerful dashboards. 
                Reduce alert fatigue, predict issues before they happen, and gain deep insights 
                into your infrastructure performance.
              </p>
              <div className="hero-buttons">
                <Link to="/pricing" className="btn btn-secondary">
                  Get Started
                  <FiArrowRight />
                </Link>
              </div>
            </motion.div>
            <motion.div
              className="hero-visual"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="dashboard-mockup">
                <div className="mockup-header">
                  <div className="mockup-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className="mockup-title">LogCrest AI Dashboard</span>
                </div>
                <div className="mockup-content">
                  <div className="alert-panel">
                    <div className="alert-item critical">
                      <FiAlertTriangle />
                      <span>High CPU usage detected</span>
                    </div>
                    <div className="alert-item warning">
                      <FiBell />
                      <span>Memory usage trending up</span>
                    </div>
                    <div className="alert-item resolved">
                      <FiCheckCircle />
                      <span>Database connection restored</span>
                    </div>
                  </div>
                  <div className="chart-placeholder">
                    <FiBarChart2 />
                    <span>Real-time Performance Metrics</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="alerting-features">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Intelligent monitoring at its finest</h2>
            <div className="grid grid-2">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="feature-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="feature-icon">
                    {feature.icon}
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="alerting-capabilities">
        <div className="container">
          <div className="capabilities-content">
            <motion.div
              className="capabilities-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Advanced AI capabilities</h2>
              <p>
                Our AI-powered alerting system learns from your infrastructure patterns 
                to provide intelligent insights and reduce false positives. Get the 
                information you need, when you need it.
              </p>
              <ul className="capabilities-list">
                {capabilities.map((capability, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiCheckCircle />
                    {capability}
                  </motion.li>
                ))}
              </ul>
              <Link to="/demo" className="btn btn-primary">
                See AI Alerting in Action
                <FiArrowRight />
              </Link>
            </motion.div>
            <motion.div
              className="capabilities-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="workflow-diagram">
                <div className="workflow-step">
                  <div className="step-icon">
                    <FiMonitor />
                  </div>
                  <div className="step-content">
                    <h4>Monitor</h4>
                    <p>Continuous data collection</p>
                  </div>
                </div>
                <div className="workflow-arrow">→</div>
                <div className="workflow-step">
                  <div className="step-icon">
                    <FiTarget />
                  </div>
                  <div className="step-content">
                    <h4>Analyze</h4>
                    <p>AI pattern recognition</p>
                  </div>
                </div>
                <div className="workflow-arrow">→</div>
                <div className="workflow-step">
                  <div className="step-icon">
                    <FiBell />
                  </div>
                  <div className="step-content">
                    <h4>Alert</h4>
                    <p>Intelligent notifications</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="alerting-benefits">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Measurable results for your team</h2>
            <div className="benefits-grid">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="benefit-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="benefit-metric">{benefit.metric}</div>
                  <div className="benefit-label">{benefit.label}</div>
                  <div className="benefit-description">{benefit.description}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="alerting-cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Ready to revolutionize your monitoring?</h2>
            <p>
              Join leading teams who trust LogCrest AI Alerting to keep their systems 
              running smoothly. Start your free trial today.
            </p>
            <div className="cta-buttons">
              <Link to="/pricing" className="btn btn-primary">
                Contact Sales
                <FiArrowRight />
              </Link>
              <Link to="/demo" className="btn btn-outline">
                Schedule Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AIAlerting;
